import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, Subject, tap } from 'rxjs';
import { ApiService } from 'src/app/common/services/api/api.service';
import { QueryFilter } from '../models/query.filter';
import { VisitorEvent } from '../../shared/models/visitor.event';
import { CalendarQueryCommand } from '../../shared/models/calendar.action.command';
import { EventTimeService } from '../../event-calendar/services/event.time.service';
import { FavoriteCustomPlace } from '../../shared/models/favorite.custom.place';
import { FavoriteCustomEvent } from '../../shared/models/favorite.custom.event';
import { BlockKeyword } from '../models/block.keyword';
import { VisitorPlace } from '../../shared/models/visitor.place';
import {
  EventLocationFilterGroup,
  PlaceLocationFilterGroup,
} from '../models/location.filter.group';
import { EditedCategory, PlaceType } from '../../shared/models/place.type';
import { ImageService } from 'src/app/common/services/image/image.service';
import { ColorStyle } from '../../shared/models/color.style';
import { EditorChoice } from '../../shared/models/editor.choice';
import { visitorPromotion } from '../../shared/models/visitor.custom.settings';
import { EditEventCommand } from '../../shared/models/edit.event.command';
import { EditPlaceCommand } from '../../shared/models/edit.place.command';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(
    private apiService: ApiService,
    private imageService: ImageService,
    private eventTimeService: EventTimeService
  ) {}

  static selectedItemIdsSubject = new BehaviorSubject<string[]>([]);
  static selectedItemIds$ = DashboardService.selectedItemIdsSubject.asObservable();
  static allIds: string[] = [];
  static loadedIds: string[] = [];
  static itemStatus$ = new BehaviorSubject<any>({});
  static changeTab$ = new BehaviorSubject<string>('');
  static dashboardStyle: 'aline' | 'corner' | 'homepage' | 'aline-lean' = 'aline-lean';
  static info$ = new Subject<any>();

  static isSelected(eventId: string): boolean {
    return DashboardService.selectedItemIdsSubject.value.some((e) => e === eventId);
  }

  static selectAll() {
    DashboardService.selectedItemIdsSubject.next([...DashboardService.allIds]);
  }

  static deSelectAll() {
    DashboardService.selectedItemIdsSubject.next([]);
  }

  static isAllSelected(): boolean {
    return DashboardService.selectedItemIdsSubject.value.length === DashboardService.allIds.length;
  }

  static addSelectedItem(id: string) {
    const current = DashboardService.selectedItemIdsSubject.value;
    if (!current.some((e) => e === id)) {
      DashboardService.selectedItemIdsSubject.next([...current, id]);
    }
  }

  static removeSelectedItem(id: string) {
    const filtered = DashboardService.selectedItemIdsSubject.value.filter((e) => e !== id);
    DashboardService.selectedItemIdsSubject.next(filtered);
  }

  saveFilter(hubName: string, filter: QueryFilter): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/filters`, filter);
  }

  deleteFilter(hubName: string, _id: string, filterType: 'events' | 'places'): Observable<any> {
    return this.apiService.delete(`hubs/${hubName}/filters?_id=${_id}&type=${filterType}`);
  }

  bulkSetApprovalStatus(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService
      .post(`hubs/${hubName}/bulk-approval-events`, {
        eventWithStatus: statusMap,
      })
      .pipe(
        tap({
          next: () => {
            DashboardService.selectedItemIdsSubject.next([]);
          },
          error: (err) => {},
        })
      );
  }
  bulkSetApprovalStatusForPlaces(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService
      .post(`hubs/${hubName}/bulk-approval-places`, {
        placeWithStatus: statusMap,
      })
      .pipe(
        tap({
          next: () => {
            DashboardService.selectedItemIdsSubject.next([]);
          },
          error: (err) => {},
        })
      );
  }

  bulkLockEvents(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-lock-events`, {
      eventWithStatus: statusMap,
    });
  }
  bulkLockPlaces(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-lock-places`, {
      placeWithStatus: statusMap,
    });
  }

  bulkPdfExportEvents(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-pdf-events`, {
      eventWithStatus: statusMap,
    });
  }
  bulkPdfExportPlaces(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-pdf-places`, {
      placeWithStatus: statusMap,
    });
  }

  bulkAddToChoiceEvents(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-add-to-choice-events`, {
      eventWithStatus: statusMap,
    });
  }
  bulkAddToChoicePlaces(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-add-to-choice-places`, {
      placeWithStatus: statusMap,
    });
  }

  bulkPinToTopEvents(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-pin-events`, {
      eventWithStatus: statusMap,
    });
  }
  bulkPinToTopPlaces(
    hubName: string,
    statusMap: {
      id: string;
      status: boolean;
    }[]
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-pin-places`, {
      placeWithStatus: statusMap,
    });
  }

  bulkBlockEvents(hubName: string, ids: string[], isBlock: boolean): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-block-events`, {
      eventIds: ids,
      isBlock,
    });
  }
  bulkBlockPlaces(hubName: string, ids: string[], isBlock: boolean): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-block-places`, {
      placeIds: ids,
      isBlock,
    });
  }

  bulkAddToItinerary(
    hubName: string,
    itineraryId: string,
    isAdd: boolean,
    itineraryInitialFirstDayAttractions?: any
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-add-to-itinerary-events`, {
      eventIds: DashboardService.selectedItemIdsSubject.value,
      itineraryId,
      isAdd,
      itineraryInitialFirstDayAttractions,
    });
  }
  bulkAddToItineraryForPlaces(
    hubName: string,
    itineraryId: string,
    isAdd: boolean,
    itineraryInitialFirstDayAttractions?: any
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-add-to-itinerary-places`, {
      placeIds: DashboardService.selectedItemIdsSubject.value,
      itineraryId,
      isAdd,
      itineraryInitialFirstDayAttractions,
    });
  }

  bulkAddToCategory(
    hubName: string,
    categoryId: string,
    isAdd: boolean,
    initialCategory?: FavoriteCustomEvent
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-add-to-category-events`, {
      eventIds: DashboardService.selectedItemIdsSubject.value,
      categoryId,
      isAdd,
      initialCategory,
    });
  }
  bulkAddToCategoryForPlaces(
    hubName: string,
    categoryId: string,
    isAdd: boolean,
    initialCategory?: FavoriteCustomPlace
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/bulk-add-to-category-places`, {
      placeIds: DashboardService.selectedItemIdsSubject.value,
      categoryId,
      isAdd,
      initialCategory,
    });
  }

  getInitialStatus(
    hubName: string,
    ids: string[],
    itemType: 'event' | 'place',
    statusType: string
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/initial-status`, {
      ids,
      itemType,
      statusType,
    });
  }

  public getHubEvents(
    hubName: string,
    paramObj: CalendarQueryCommand
  ): Observable<{ total: number; data: any[]; allIds: string[] }> {
    return this.apiService.get(`hubs/${hubName}/events/v3`, paramObj).pipe(
      tap((response) => {
        DashboardService.allIds = response.allIds; // for selecting all filtered events/places
      }),
      map(({ total, data, allIds }: { total: number; data: any[]; allIds: string[] }) => {
        data.forEach((event) => {
          this.eventTimeService.processEventTimes(event);
        });
        return {
          total,
          data,
          allIds,
        };
      })
    );
  }

  public editEvent(hubName: string, command: EditEventCommand) {
    return this.apiService.put(`hubs/${hubName}/events/${command._id}/`, command);
  }

  /**
   *  Tourist Attraction => tourist_attraction
   * */
  toCategoryCode(category: string) {
    return category.toLowerCase().trim().replaceAll(' ', '_');
  }

  public editPlace(hubName: string, command: EditPlaceCommand) {
    const categoryCode = command.categoryText
      .split(', ')
      .map((x) => {
        return this.toCategoryCode(x);
      })
      .join(', ');
    command.category = categoryCode;

    return this.apiService.put(`hubs/${hubName}/places/${command.placeId}/`, command);
  }

  public getSimilarEvents(hubName: string): Observable<{
    similarEventsByExactSame: {
      group: VisitorEvent[];
      condition: any[];
    }[];
    similarEventsBy2ParamSimilarity: {
      group: VisitorEvent[];
      condition: any[];
    }[];
    similarMultiDatesEvents: {
      group: VisitorEvent[];
      condition: any[];
    }[];
  }> {
    return this.apiService.get(`hubs/${hubName}/similar`).pipe(
      map((data) => {
        data.similarEventsByExactSame.forEach((x) =>
          x.group.forEach((item) => {
            this.eventTimeService.processEventTimes(item);
          })
        );
        data.similarMultiDatesEvents.forEach((x) =>
          x.group.forEach((item) => {
            this.eventTimeService.processEventTimes(item);
          })
        );
        return {
          similarEventsByExactSame: data.similarEventsByExactSame,
          similarEventsBy2ParamSimilarity: data.similarEventsBy2ParamSimilarity,
          similarMultiDatesEvents: data.similarMultiDatesEvents,
        };
      })
    );
  }

  public getSimilarPlaces(hubName, group): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/similar-places?group=${group}`);
  }

  public getHubPlaceIds(hubName: string, paramObj: CalendarQueryCommand): Observable<any> {
    return this.apiService
      .post(`hubs/${hubName}/places/v3`, { ...paramObj, isOnlyGetAllMatchedIds: true })
      .pipe(
        tap((response) => {
          DashboardService.allIds = response.allIds; // for selecting all filtered events/places
        }),
        map(({ total, data, allIds }: { total: number; data: any[]; allIds: string[] }) => {
          return {
            total,
            data,
            allIds,
          };
        })
      );
  }

  public getNoDescriptionPlaces(hubName): Observable<VisitorPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/no-description`);
  }

  public getHubPlaceByIds(
    hubName: string,
    placeIds: string[],
    projection: string
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/places/by-ids`, { placeIds, projection }).pipe(
      tap((response) => {
        console.log(response);
      })
    );
  }
  public getHubEventByIds(
    hubName: string,
    eventIds: string[],
    projection: string
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/events/by-ids`, { eventIds, projection }).pipe(
      tap((response) => {
        console.log(response);
      })
    );
  }

  public getHubPlaces(
    hubName: string,
    paramObj: CalendarQueryCommand
  ): Observable<{ total: number; data: any[]; allIds: string[]; batchIds: string[] }> {
    return this.apiService.post(`hubs/${hubName}/places/v3`, paramObj).pipe(
      tap((response) => {
        // DashboardService.allIds = response.allIds; // for selecting all filtered events/places
        if (response.batchIds && response.batchIds.length > 0) {
          DashboardService.loadedIds = [...DashboardService.loadedIds, ...response.batchIds]; // for selecting all filtered events/places
        }
      }),
      map(
        ({
          total,
          data,
          allIds,
          batchIds,
        }: {
          total: number;
          data: any[];
          allIds: string[];
          batchIds: string[];
        }) => {
          return {
            total,
            data,
            allIds,
            batchIds,
          };
        }
      )
    );
  }

  approveEvent(hubName: string, eventId: string) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/approve`);
  }

  unApproveEvent(hubName: string, eventId: string) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-approve`);
  }

  approvePlace(hubName: string, placeId: string) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/approve`);
  }

  unApprovePlace(hubName: string, placeId: string) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-approve`);
  }

  blockPlace(hubName: string, placeId: string) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/block`);
  }

  saveSpiderEventPages(hubName: string, url: string) {
    return this.apiService.post(`hubs/${hubName}/spider-event-pages`, { url });
  }

  editSpiderEventPage(hubName: string, oldUrl: string, newUrl: string) {
    return this.apiService.put(`hubs/${hubName}/edit-spider-event-page`, { oldUrl, newUrl });
  }

  getSpiderEventPages(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/spider-event-pages`).pipe(
      map((data) => {
        data.pages = data.pages
          .filter((x) => x.socialName.toLowerCase().indexOf('facebook') >= 0)
          .map((x) => {
            return {
              url: x.url.replace('https://facebook.com/', 'https://www.facebook.com/').trim(),
              ...x,
            };
          })
          .sort((a, b) => {
            return a.url.toLowerCase().localeCompare(b.url.toLowerCase());
          });
        return data;
      })
    );
  }

  deleteSpiderEventPage(hubName: string, page: string) {
    return this.apiService.put(`hubs/${hubName}/delete-spider-event-page`, { page });
  }

  deleteBlockKeyword(hubName: string, command: BlockKeyword): Observable<void> {
    return this.apiService.delete(
      `hubs/${hubName}/block-keywords?type=${command.blackType}&value=${command.blackValue}`
    );
  }

  addBlockKeyword(hubName: string, command: BlockKeyword): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/block-keywords`, {
      type: command.blackType,
      value: command.blackValue,
    });
  }

  updateBlockedEventDomains(hubName: string, command: string[]): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/block-event-domains`, command);
  }

  getBlockedEvents(hubName: string): Observable<VisitorEvent[]> {
    return this.apiService.get(`hubs/${hubName}/events/block`);
  }

  unBlockEvent(hubName: string, eventId: string) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-block`);
  }
  unBlockPlace(hubName: string, placeId: string) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-block`);
  }

  deleteEvent(hubName: string, eventId: string) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/delete-permanently`);
  }
  deletePlace(hubName: string, placeId: string) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/delete-permanently`);
  }

  removeLocationFeed(
    hubName: string,
    feedId: string,
    socialName: string = 'google'
  ): Observable<void> {
    return this.apiService.delete(`hubs/${hubName}/location-feed/${feedId}/${socialName}`);
  }

  updateGoogleLocationFeed(
    hubName,
    command: {
      id: string;
      categories: string[];
      radius: number;
      isRepullExistingCategories: boolean;
      points: any;
      filterByCity: string;
    }
  ): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/google-location-feeds`, command, {
      timeout: 1200 * 1000,
    });
  }

  checkPullingGooglePlaceStatus(hubName): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/check-google-places-pulling-status`);
  }

  searchGooglePlaceByName(hubName, placeName): Observable<any> {
    return this.apiService.get(
      `hubs/${hubName}/places/search-google-place-by-name?placeName=${encodeURIComponent(
        placeName
      )}`
    );
  }

  getAllowedPlaceCategories(hubName: string): Observable<string[]> {
    return this.apiService.get(`hubs/${hubName}/places/allowed-place-categories`);
  }

  saveGooglePlace(hubName, isApproved: boolean, place: any): Observable<any> {
    place.isApproved = isApproved;
    return this.apiService.post(`hubs/${hubName}/places/save-google-place`, place);
  }

  setCalendarStyle(
    hubName: string,
    calendarStyle: { event: string; place: string }
  ): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/calendar-style`, { calendarStyle });
  }

  addClientPlaceBlockKeyword(hubName: string, command: string[]): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/client-place-block-keywords`, command);
  }

  getClientPlaceBlockKeywordsPlaceList(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/blocked-places-by-client-keywords`);
  }

  getGlobalBlockedByKeywordsPlaces(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/blocked-by-global-keywords`);
  }

  getBlockPlaces(hubName: string): Observable<VisitorPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/block`);
  }

  setCustomPlaceUseGroupFilter(hubName: string, isCityLevelFilter: boolean): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-location-filter-city-level`, {
      useGroupFilter: isCityLevelFilter,
    });
  }

  setCustomPlaceGroups(
    hubName: string,
    customPlaceGroups: PlaceLocationFilterGroup[],
    isCityLevelFilter
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-location-filter-groups`, {
      customPlaceGroups,
      isCityLevelFilter,
    });
  }

  setCustomEventGroups(
    hubName: string,
    customEventGroups: EventLocationFilterGroup[],
    isCityLevelFilter
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-event-location-filter-groups`, {
      customEventGroups,
      isCityLevelFilter,
    });
  }

  getPlaceLocationFilters(hubName: string) {
    return this.apiService.get(`visitors/${hubName}/places/location-filter`);
  }

  getEventLocationFilters(hubName) {
    return this.apiService.get(`visitors/${hubName}/events/location-filter`);
  }

  getPlacesByPlaceIds(hubName: string, placeIds: string[]): Observable<VisitorPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/placeIds`, { placeIds: placeIds });
  }

  setCustomEvent(
    hubName: string,
    command: any
  ): Observable<{ events: FavoriteCustomEvent[]; places: FavoriteCustomPlace[] }> {
    return this.apiService.put(`hubs/${hubName}/custom-events`, {
      isTurnOn: command.isTurnOn,
      customEvents: command.customEvents,
    });
  }

  setCustomPlace(
    hubName: string,
    command: any
  ): Observable<{ events: FavoriteCustomEvent[]; places: FavoriteCustomPlace[] }> {
    return this.apiService.put(`hubs/${hubName}/custom-places`, {
      customPlaces: command.customPlaces,
      isTurnOn: command.isTurnOn,
    });
  }

  setCustomDefaultPlaceCategory(
    hubName: string,
    customDefaultPlaceCategory: PlaceType[],
    editedCategories: EditedCategory[]
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-categories`, {
      customDefaultPlaceCategory,
      editedCategories,
    });
  }

  setUseDefaultPlaceCategory(hubName: string): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-use-default`);
  }

  setLogoFile(hubName: string, command: any): Observable<any> {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }

    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.put(`hubs/${hubName}/logo-file`, formData);
  }

  setLogo(hubName: string, logo: any): Observable<void> {
    // this.imageService.
    return this.apiService.put(`hubs/${hubName}/logo`, logo);
  }

  setMenuStyle(hubName: string, isSideBar: Boolean): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/menu-style`, { isSideBar: isSideBar });
  }

  setStyle(hubName: string, style: ColorStyle): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/style`, style);
  }

  setIsHideEditorChoice(hubName: string, isHideEditorChoice: Boolean): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/is-hide-editor-choice`, {
      isHideEditorChoice: isHideEditorChoice,
    });
  }

  saveEditorChoice(hubName: string, params: EditorChoice): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/editor-choice`, params);
  }

  setPromotion(hubName: string, command: visitorPromotion) {
    return this.apiService.put(`hubs/${hubName}/promotion`, command);
  }

  setTripPlannerSettings(hubName: string, settings: any): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/trip-planner-settings`, settings);
  }

  getTravelBuddyClientUploadedFiles(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/travel-buddy-client-uploaded-files`);
  }

  uploadTravelBuddyClientFiles(hubName: string, files: File[]) {
    let fd = new FormData();
    for (const file of files) {
      fd.append('files', file);
    }
    return this.apiService.post(`hubs/${hubName}/travel-buddy-upload-client-files`, fd);
  }

  uploadLinkContent(hubName: string, link: string, content: string) {
    return this.apiService.post(`hubs/${hubName}/travel-buddy-save-link-content`, {
      link,
      content,
    });
    // .toPromise();
  }

  extractContentFromLink(hubName: string, link: string, isIncludeLinks = false) {
    return this.apiService
      .post(`hubs/${hubName}/extract-content-from-link`, { link, isIncludeLinks })
      .toPromise();
  }

  deleteTravelBuddyClientFile(hubName: string, fileId: string) {
    return this.apiService.delete(`hubs/${hubName}/travel-buddy-client-uploaded-file`, { fileId });
  }

  setTravelBuddyUI(hubName: string, ui: any) {
    return this.apiService.put(`hubs/${hubName}/travel-buddy-ui`, ui);
  }

  setTravelBuddyIndicator(hubName: string, indicator: any) {
    return this.apiService.put(`hubs/${hubName}/travel-buddy-indicator`, indicator);
  }

  setPDFEventHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/event-header-pdf-image`, pdfSettings);
  }

  setPDFPlaceHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/place-header-pdf-image`, pdfSettings);
  }

  setPDFTourHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/tour-header-pdf-image`, pdfSettings);
  }

  setPDFTripHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/trip-header-pdf-image`, pdfSettings);
  }

  setIsKeepTempClosedPlaceInApproved(hubName: string, isKeep: boolean): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/is-keep-temp-closed-place-in-approved`, { isKeep });
  }

  getStyleAndPDFSettingsForHub(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/style-and-pdf-settings`);
  }

  addAttractionToTour(
    hubName: string,
    itineraryId: string,
    item: { feedId: string; feedType: 'event' | 'place' }
  ): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/tours/add-attraction-v3`, {
      id: itineraryId,
      feedId: item.feedId,
      feedType: item.feedType,
    });
  }

  removeAttractionFromTour(
    hubName: string,
    itineraryId: string,
    item: { feedId: string; feedType: 'event' | 'place' }
  ): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/tours/delete-attraction-v3`, {
      id: itineraryId,
      attractionId: item.feedId,
      feedType: item.feedType,
    });
  }

  addEventToCustomBtn(hubName: string, eventId: string, btnIndex: number): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/add-to-custom-event`, {
      btnIndex,
    });
  }

  removeEventFromCustomBtn(hubName: string, eventId: string, btnIndex: number): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/remove-from-custom-event`, {
      btnIndex,
    });
  }

  addPlaceToCustomBtn(hubName: string, placeId: string, btnIndex: number): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/add-to-custom-place`, {
      btnIndex,
    });
  }

  removePlaceFromCustomBtn(hubName: string, placeId: string, btnIndex: number): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/remove-from-custom-place`, {
      btnIndex,
    });
  }

  resizeImageFromUnApprovedEvent(
    hubName: string,
    eventId: string
  ): Observable<{ success: boolean; url: string; error: any }> {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/enlarge-img`);
  }

  setDefaultPlaceSorting(hubName: string, sorting: string) {
    return this.apiService.put(`hubs/${hubName}/default-place-sorting`, { sorting });
  }

  updateDashboardSettings(hubName: string, settings: any) {
    return this.apiService.put(`hubs/${hubName}/dashboard-settings`, settings);
  }

  savePlacesDescriptions(
    hubName: string,
    placesWithDescription: { _id: string; name: string; description: string }[]
  ): Observable<VisitorPlace[]> {
    return this.apiService.post(`hubs/${hubName}/places/save-description`, {
      placesWithDescription,
    });
  }
  saveEventsDescriptions(
    hubName: string,
    eventsWithDescription: { _id: string; name: string; description: string }[]
  ): Observable<VisitorEvent[]> {
    return this.apiService.post(`hubs/${hubName}/events/save-description`, {
      eventsWithDescription,
    });
  }

  aiAssistant(hubName: string, query: any) {
    return this.apiService.post(`visitors/${hubName}/ai-assistant`, query);
  }
}
